import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import { BaseChartColors, BaseFontColors } from "../constants";
import { BaseDateChartData, TransformedChartData } from "./types";
import { formatPenetrationPercentage } from "../../../pages/Workspace/utils";

const getFieldId = (name: string): string => {
  return `series_${name.toLowerCase().replace(/\W+/g, "_")}`;
};

export const configureChartData = (
  chart: am4charts.XYChart,
  chartData: BaseDateChartData[]
) => {
  const transformedData: TransformedChartData[] = chartData.map((point) => {
    const result: TransformedChartData = { date: point.date };

    for (const series of point.series) {
      const fieldId = getFieldId(series.name);
      result[fieldId] = series.value;
      result[`${fieldId}_count`] = series.companyCount || 0;
    }

    return result;
  });

  chart.data = transformedData;
};

export const createSeriesConfigs = (chartData: BaseDateChartData[]) => {
  const uniqueSeriesNames = new Set<string>();
  for (const point of chartData) {
    for (const series of point.series) {
      uniqueSeriesNames.add(series.name);
    }
  }

  const seriesConfigs = Array.from(uniqueSeriesNames).map((name) => ({
    name,
    fieldId: getFieldId(name),
  }));

  const finalDataPoint = chartData[chartData.length - 1];
  seriesConfigs.sort((a, b) => {
    const aSeriesInfo = finalDataPoint.series.find((s) => s.name === a.name);
    const bSeriesInfo = finalDataPoint.series.find((s) => s.name === b.name);

    const aValue = aSeriesInfo ? aSeriesInfo.value : 0;
    const bValue = bSeriesInfo ? bSeriesInfo.value : 0;

    return bValue - aValue;
  });

  return seriesConfigs;
};

export const configureLineColors = (
  chart: am4charts.XYChart,
  seriesConfigs: ReturnType<typeof createSeriesConfigs>,
  _data: BaseDateChartData[],
  baseColor?: BaseChartColors | string[],
  showCustomTooltip?: boolean
) => {
  seriesConfigs.forEach((seriesConfig, index) => {
    const series = chart.series.push(new am4charts.LineSeries());
    series.connect = true;
    series.dataFields.valueY = seriesConfig.fieldId;
    series.dataFields.dateX = "date";
    series.name = seriesConfig.name;
    if (showCustomTooltip) {
      series.adapter.add("tooltipText", (_, target) => {
        const value = target.tooltipDataItem?.values.valueY.value || 0;
        const formattedValue = formatPenetrationPercentage(value);

        return `[bold]{name}[/]
        Penetration: ${formattedValue}%
        # of companies: {${`${seriesConfig.fieldId}_count`}}`;
      });
    } else {
      series.tooltipText = `{name}: {valueY}`;
    }

    series.strokeWidth = 2;
    series.dy = -3;
    let seriesColor;
    if (Array.isArray(baseColor)) {
      seriesColor = am4core.color(baseColor[index % baseColor.length]);
    } else {
      const baseColorObj = am4core.color(baseColor || BaseChartColors.Blue);
      const lightenFactor = 0.15 + 0.08 * index;
      seriesColor = baseColorObj.lighten(lightenFactor);
    }
    series.stroke = seriesColor;
    if (series.tooltip) {
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = seriesColor;
      series.tooltip.label.fill = am4core.color("#ffffff");
    }

    const bullet = series.bullets.push(new am4charts.CircleBullet());
    bullet.circle.strokeWidth = 2;
    bullet.circle.radius = 4;
    bullet.circle.fill = am4core.color("#fff");
    bullet.circle.stroke = series.stroke;
    bullet.dy = -3;
    const hoverState = bullet.states.create("hover");
    hoverState.properties.scale = 1.7;
  });
};

export const createDateAxis = (chart: am4charts.XYChart) => {
  const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
  dateAxis.baseInterval = { timeUnit: "month", count: 1 };
  dateAxis.renderer.minGridDistance = 50;
  dateAxis.renderer.grid.template.location = 0;
  dateAxis.startLocation = 0;
  dateAxis.endLocation = 1;

  dateAxis.dateFormats.setKey("month", "MMM yyyy");
  dateAxis.periodChangeDateFormats.setKey("month", "MMM yyyy");

  dateAxis.renderer.labels.template.fontSize = 11;
  dateAxis.renderer.labels.template.fill = am4core.color(
    BaseFontColors.TextDark
  );
  if (dateAxis.tooltip) {
    dateAxis.tooltip.disabled = true;
  }
};

export const createValueAxis = (
  chart: am4charts.XYChart,
  showPercentageSymbol: boolean,
  yAxisLabel?: string
) => {
  const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  valueAxis.title.text = yAxisLabel || "";
  valueAxis.renderer.minGridDistance = 30;

  valueAxis.extraMax = 0.1;
  const hasZeroValue = chart.data.some((dataPoint) =>
    Object.entries(dataPoint)
      .filter(([key]) => key !== "date")
      .some(([, value]) => value === 0)
  );
  if (hasZeroValue) {
    valueAxis.min = -0.5;
    valueAxis.strictMinMax = true;
  }

  valueAxis.renderer.grid.template.adapter.add("strokeDasharray", () => {
    return "3,3";
  });

  valueAxis.renderer.labels.template.fontSize = 11;
  valueAxis.renderer.labels.template.fill = am4core.color(
    BaseFontColors.TextDark
  );

  if (showPercentageSymbol) {
    valueAxis.numberFormatter = new am4core.NumberFormatter();
    valueAxis.numberFormatter.numberFormat = "#.#";

    valueAxis.adapter.add("getTooltipText", (text) => `${text}%`);

    valueAxis.renderer.labels.template.adapter.add("text", (text) => {
      return `${text}%`;
    });
  }

  if (valueAxis.tooltip) {
    valueAxis.tooltip.disabled = true;
  }
};

export const createLegends = (chart: am4charts.XYChart) => {
  chart.legend = new am4charts.Legend();
  chart.legend.position = "bottom";
  chart.legend.align = "right";
  chart.legend.contentAlign = "right";
  chart.legend.minHeight = 80;
  chart.legend.maxHeight = 80;
  chart.legend.scrollable = true;

  chart.legend.markers.template.width = 16;
  chart.legend.markers.template.height = 4;

  chart.legend.useDefaultMarker = true;
  chart.legend.markers.template.children.clear();

  const rectMarker = chart.legend.markers.template.createChild(
    am4core.RoundedRectangle
  );
  rectMarker.strokeWidth = 0;
  rectMarker.width = 16;
  rectMarker.height = 4;
  rectMarker.adapter.add("fill", (fill, target) => {
    const series =
      target.dataItem && (target.dataItem.dataContext as am4charts.LineSeries);
    if (series && series.stroke) {
      return series.stroke;
    }
    return fill;
  });

  let legendSorted = false;

  chart.events.on("datavalidated", () => {
    if (legendSorted) return;

    const { legend } = chart;
    const { values: items } = legend.itemContainers;

    if (items && items.length > 0) {
      // Sort items by series name
      items.sort((a, b) => {
        const aName =
          (a.dataItem?.dataContext as am4charts.LineSeries)?.name || "";
        const bName =
          (b.dataItem?.dataContext as am4charts.LineSeries)?.name || "";
        return aName.localeCompare(bName);
      });

      let index = 0;
      for (const item of items) {
        if (item.parent) {
          item.parent.children.moveValue(item, index);
        }
        index++;
      }

      legendSorted = true;
    }
  });

  chart.legend.dx = 75;
  chart.series.each((series) => {
    series.events.on("shown", () => {
      const { legendDataItem } = series;
      if (legendDataItem) {
        const marker = legendDataItem.marker.children.getIndex(0);
        if (marker instanceof am4core.RoundedRectangle) {
          marker.fill = series.stroke;
        }
      }
    });
  });

  chart.legend.itemContainers.template.events.on("over", (event) => {
    const itemContainer = event.target;
    const series = itemContainer.dataItem?.dataContext as am4charts.LineSeries;

    if (series) {
      chart.series.each((otherSeries) => {
        if (otherSeries !== series) {
          otherSeries.opacity = 0.3;
          for (const bullet of otherSeries.bullets.values) {
            bullet.opacity = 0.3;
          }
        }
      });
      series.opacity = 1;
      for (const bullet of series.bullets.values) {
        bullet.opacity = 1;
      }
      series.strokeWidth = 4;
    }
  });

  chart.legend.itemContainers.template.events.on("out", () => {
    chart.series.each((series) => {
      series.opacity = 1;
      series.strokeWidth = 2;
      for (const bullet of series.bullets.values) {
        bullet.opacity = 1;
      }
    });
  });
};
